import React, {
  FunctionComponent,
  useEffect /*, SyntheticEvent */,
} from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { Container, Grid, Modal } from 'semantic-ui-react';
import { useMutation } from '@apollo/react-hooks';
import withStyles from 'react-jss';
import { pathOr } from 'ramda';
import classnames from 'classnames';

import { ClassProp } from 'helpers/types';
import { formatDate } from 'helpers/date';
import Button from 'components/Button';
import FormDateInput from 'components/FormComponents/FormDateInput';
import FormInput from 'components/FormComponents/FormInput';
import {
  handleSuccess,
  handleError,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.helpers';

import {
  AdjustBudgetRequest,
  AdjustBudgetResult,
  Budget,
} from '../../MerchantCampaigns.helpers';

import BUDGET_ADJUST from './AddBudget.mutations';
import styles from './AddBudget.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;
type Props = {
  budgets: Budget[];
  classes: Styles;
};

const defaultSuccessMessage = [
  {
    displayMessage: 'Success!',
    message: 'Budget Added Successfully.',
  },
];

const AddBudget: FunctionComponent<Props> = ({ budgets, classes }) => {
  const budget = pathOr({}, [0], budgets);
  const currentDate = new Date();
  const methods = useForm<AdjustBudgetRequest>({
    mode: 'onBlur',
    defaultValues: {
      approvalDate: currentDate,
    },
  });

  const { register, handleSubmit, errors } = methods;
  useEffect(() => {
    register(
      { name: 'insertionOrderRef' },
      { required: 'Insertion order ref is required' },
    );
    register(
      { name: 'budgetAdjustmentName' },
      { required: 'Adjustment name is required' },
    );
    register(
      { name: 'approvalDate' },
      { required: 'Approval date is required' },
    );
  }, [register]);

  const handleSuccessResponse = (response: AdjustBudgetResult) => {
    handleSuccess(defaultSuccessMessage);
  };

  const [runMutation, { loading: isSubmitting }] = useMutation(BUDGET_ADJUST, {
    onCompleted: handleSuccessResponse,
    onError: handleError,
    awaitRefetchQueries: true,
  });

  const onSubmit = ({
    budgetUuid,
    insertionOrderRef,
    budgetAdjustmentName,
    amount,
    approvalDate,
  }: AdjustBudgetRequest) => {
    const AdjustBudgetRequest = {
      budgetUuid,
      insertionOrderRef,
      budgetAdjustmentName,
      amount: parseInt((amount as unknown) as string),
      approvalDate: formatDate(approvalDate),
    };

    runMutation({
      variables: {
        params: { ...AdjustBudgetRequest },
      },
    });

    return;
  };

  return (
    <Modal
      size={'large'}
      trigger={<Button>Add Budget</Button>}
      closeIcon
      className={classes.modal}
    >
      <Modal.Header>Add Budget</Modal.Header>
      <Modal.Content>
        <Container className={classes.container}>
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <input
                type="hidden"
                value={budget.uuid}
                name="budgetUuid"
                ref={register}
              />
              <Grid>
                <Grid.Row className={classes.formRow}>
                  <Grid.Column width={4}>
                    <FormInput
                      name="insertionOrderRef"
                      type="text"
                      placeholder="IO Ref Number"
                      label="Insertion Order"
                      register={register}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormInput
                      name="budgetAdjustmentName"
                      type="text"
                      placeholder="Adjustment Name"
                      label="Adjustment Name"
                      register={register}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormInput
                      name="amount"
                      type="number"
                      placeholder="Dollar Amt"
                      label="Budget Amount"
                      register={register({
                        required: 'Budget amount is required.',
                        validate: {
                          oneOf: (value: string) =>
                            !Number.isNaN(parseFloat(value)) ||
                            'Invalid amount',
                        },
                      })}
                    />
                  </Grid.Column>
                  <Grid.Column width={4}>
                    <FormDateInput
                      name="approvalDate"
                      label="Approval Date"
                      value={currentDate}
                    />
                  </Grid.Column>
                </Grid.Row>
                <Grid.Row className={classes.formRow}>
                  <button
                    tabIndex={8}
                    type="submit"
                    disabled={isSubmitting || Object.keys(errors).length > 0}
                    className={classnames(classes.button, classes.mediumBtn)}
                  >
                    {isSubmitting ? '...Submitting' : 'Save'}
                  </button>
                </Grid.Row>
              </Grid>
            </form>
          </FormProvider>
        </Container>
      </Modal.Content>
    </Modal>
  );
};

export default withStyles(styles)(AddBudget);
